import React, { useState, useEffect, useRef, useContext, useCallback, useMemo } from "react"
import {
	Typography,
	Button,
	Upload,
	message,
	Space,
	Tabs,
	Table,
	Dropdown,
	Menu,
	Progress,
	DatePicker,
	Empty,
	AutoComplete,
	Select,
} from "antd"
import { UploadOutlined, PlusOutlined, CheckCircleOutlined, CloseCircleOutlined, DownOutlined } from "@ant-design/icons"
import apiConstants from "../../api"
import axios from "axios"
import "./style.css"
import { GlobalStoreContext } from "../../Store"
import {
	validateFile,
	isServierClient,
	STATEMENT_TYPES,
	MONTH_OPTIONS,
	SERVIER_FIELD_LABELS,
	getFinancialYearOptions,
	getDefaultServierFields,
	getInvalidServierFields,
	appendServierFields,
	getUploadErrorMessage,
} from "./uploadHelpers"

const { Title, Text } = Typography
const { TabPane } = Tabs

// Removed initialState as we're using GlobalStoreContext for authentication

const UploadAndMapStockists = () => {
	// Get global context functions and state
	const { getStockistEntries, failMessage, authToken } = useContext(GlobalStoreContext)

	// State variables
	const [selectedClient, setSelectedClient] = useState({ name: "None" })
	// Servier only: values copied to rows by "Apply to all files" and used for new rows
	const [servierDefaults, setServierDefaults] = useState(getDefaultServierFields)
	const [mappings, setMappings] = useState(() => [
		{
			id: 1,
			file: null,
			stockistId: null,
			date: null,
			uploading: false,
			progress: 0,
			uuid: null,
			...getDefaultServierFields(),
		},
	])
	const [activeTab, setActiveTab] = useState("1")
	const [results, setResults] = useState({ success: [], errors: [] })
	const [stockistOptions, setStockistOptions] = useState([])
	const isServier = isServierClient(selectedClient)
	const financialYearOptions = useMemo(
		() => getFinancialYearOptions().map((fy) => ({ value: fy, label: fy })),
		[]
	)
	// No longer needed as we get authToken from GlobalStoreContext
	// const [authToken] = useState(initialState.authToken)

	// Create refs to prevent ResizeObserver errors
	const containerRef = useRef(null)
	const debounceTimeoutRef = useRef(null)

	// Debounced state update function to prevent rapid DOM changes
	const debounceSetMappings = useCallback((updateFunction) => {
		if (debounceTimeoutRef.current) {
			clearTimeout(debounceTimeoutRef.current)
		}
		debounceTimeoutRef.current = setTimeout(() => {
			setMappings(updateFunction)
		}, 16) // 60fps = ~16ms
	}, [])

	// Handle stockist search for autocomplete - using useCallback to prevent recreation on every render
	const handleStockistSearch = useCallback(
		async (searchText) => {
			if (!selectedClient || selectedClient.name === "None") return

			try {
				const response = await getStockistEntries({
					client: { ...selectedClient },
					filter: {
						text: searchText,
					},
				})

				if (response && Array.isArray(response)) {
					setStockistOptions(
						response.map((stockistId) => ({
							value: stockistId,
							label: stockistId,
						}))
					)
				}
			} catch (error) {
				console.error("Error searching stockists:", error)
				setStockistOptions([])
				failMessage(error.message || "Failed to search stockists")
			}
		},
		[selectedClient, getStockistEntries, failMessage]
	)

	// Handle client selection change - using useCallback to prevent re-creation on every render
	const handleClientChange = useCallback((e) => {
		setSelectedClient(apiConstants.SSE_CLIENTS.find((c) => c.name === e.key.split("_")[0]))
	}, [])

	// Client dropdown menu - memoized to prevent unnecessary re-renders
	const clientMenu = useMemo(() => (
		<Menu>
			{apiConstants.SSE_CLIENTS.map((e, idx) => (
				<Menu.Item key={e.name + "_" + idx} onClick={handleClientChange}>
					{e.name}
				</Menu.Item>
			))}
		</Menu>
	), [handleClientChange])

	// Add a new row for mapping
	const addNewRow = () => {
		setMappings((prevMappings) => {
			const newId = prevMappings.length > 0 ? Math.max(...prevMappings.map((m) => m.id)) + 1 : 1
			const newMapping = {
				id: newId,
				file: null,
				stockistId: null,
				date: null,
				uploading: false,
				progress: 0,
				uuid: null,
				...servierDefaults,
			}
			const newMappings = [...prevMappings, newMapping]
			console.log("mappings after adding new:", newMappings, "new mapping id:", newId)
			return newMappings
		})
	}

	// Remove a mapping row
	const removeRow = (id) => {
		if (mappings.length > 1) {
			setMappings((prevMappings) => {
				const newMappings = prevMappings.filter((mapping) => mapping.id !== id)
				console.log("mappings after deletion:", newMappings, "deleted id:", id)
				return newMappings
			})
		} else {
			message.warning("At least one mapping is required")
		}
	}

	// Reset a file upload for a mapping row
	const resetFileUpload = (e, mappingId) => {
		// Prevent event propagation to avoid triggering other handlers
		if (e) {
			e.preventDefault();
			e.stopPropagation();
		}

		setMappings((prevMappings) => {
			const newMappings = prevMappings.map((mapping) =>
				mapping.id === mappingId ? { 
					...mapping, 
					file: null, 
					uploading: false, 
					progress: 0, 
					uuid: null, 
					showUuid: false,
					duplicateFile: false,
					duplicateUuid: null,
					uploadError: null,
					showFieldErrors: false
				} : mapping
			)
			return newMappings
		})
	}

	// Handle file upload for a specific row
	const handleFileChange = (info, mappingId) => {
		// If this is a remove event, ignore it - we'll handle file removal separately
		if (info.file.status === 'removed') {
			return;
		}

		uploadFile(info.file, mappingId)
	}

	// Upload a file for a row. Also used by "Retry" on a failed Servier row.
	const uploadFile = async (file, mappingId) => {
		// Ensure a client is selected
		if (selectedClient.name === "None") {
			message.error("Please select a client first")
			return
		}

		// Check file extension and size
		const fileError = validateFile(file)
		if (fileError) {
			message.error(fileError)
			return
		}

		// Servier: statement type, month and financial year must be set on the row first
		const row = mappings.find((mapping) => mapping.id === mappingId)
		if (isServier) {
			const invalidFields = getInvalidServierFields(row)
			if (invalidFields.length > 0) {
				const fieldNames = invalidFields.map((field) => SERVIER_FIELD_LABELS[field]).join(", ")
				message.error(`Set ${fieldNames} before uploading ${file.name}`)
				setMappings((prevMappings) =>
					prevMappings.map((mapping) =>
						mapping.id === mappingId ? { ...mapping, showFieldErrors: true } : mapping
					)
				)
				return
			}
		}

		// Update the specific mapping row with file information
		setMappings((prevMappings) => {
			const newMappings = prevMappings.map((mapping) =>
				mapping.id === mappingId
					? { ...mapping, file, uploading: true, progress: 0, uploadError: null, showFieldErrors: false }
					: mapping
			)
			console.log("mappings after file selection:", newMappings, "file:", file.name, "mappingId:", mappingId)
			return newMappings
		})

		// Prepare form data for upload
		const formData = new FormData()
		formData.append("file", file)
		if (isServier) {
			appendServierFields(formData, row)
		}

		// Servier: keep the file on the row with the error so it can be retried
		const markServierFailed = (errorMessage) => {
			message.error(`Failed to upload ${file.name}: ${errorMessage}`)
			setMappings((prevMappings) =>
				prevMappings.map((mapping) =>
					mapping.id === mappingId
						? { ...mapping, uploading: false, file, progress: 0, uploadError: errorMessage }
						: mapping
				)
			)
		}

		try {
			// Use the client-specific host URL
			const uploadEndpoint = `${selectedClient.hostUrl}/AdminUtility/v0.1/upload-file`

			// Log client and endpoint information for debugging
			console.log(`Uploading file for client: ${selectedClient.name} to endpoint: ${uploadEndpoint}`);

			// Upload the file with progress tracking
			const response = await axios.post(uploadEndpoint, formData, {
				headers: {
					"Content-Type": "multipart/form-data",
					Authorization: authToken,
				},
				onUploadProgress: (progressEvent) => {
					const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)

					// Use debounced update for progress to prevent rapid DOM changes
					debounceSetMappings((prevMappings) => {
						const newMappings = prevMappings.map((mapping) =>
							mapping.id === mappingId ? { ...mapping, progress: percentCompleted } : mapping
						)
						console.log(
							"mappings after progress update:",
							newMappings,
							"progress:",
							percentCompleted,
							"mappingId:",
							mappingId
						)
						return newMappings
					})
				},
			})

			// Log complete response data for debugging
			console.log(`Upload response for ${selectedClient.name}:`, response.data);
			console.log(`UUID from response: ${response.data?.file?.uuid}`);

			// Handle successful upload
			if (response.data.duplicateOf) {
				message.warning(
					`File ${file.name} is a duplicate of an existing file: ${response.data.duplicateOf.uuid}`,
					10
				)
				setMappings((prevMappings) => {
					const newMappings = prevMappings.map((mapping) =>
						mapping.id === mappingId ? { 
							...mapping, 
							uploading: false, 
							file, 
							progress: 0,
							duplicateFile: true,
							duplicateUuid: response.data.duplicateOf.uuid
						} : mapping
					)
					console.log("mappings after duplicate handling:", newMappings, "mappingId:", mappingId)
					return newMappings
				})
			} else if (isServier && response.data.ok !== true) {
				markServierFailed(response.data.message || "Upload was not accepted")
			} else {
				message.success(`${file.name} uploaded successfully`)
				
				// Extract UUID from response - log the data to debug the issue
				const fileUuid = response.data?.file?.uuid;
				console.log("File UUID from response:", fileUuid);
				console.log("Complete response data:", response.data);
				
				// Ensure we have a valid UUID before updating the state
				if (!fileUuid) {
					console.warn("Warning: No UUID found in file response, attempting to find it elsewhere in the response");
				}
				
				setMappings((prevMappings) => {
					const newMappings = prevMappings.map((mapping) =>
						mapping.id === mappingId
							? {
									...mapping,
									uploading: false,
									progress: 100,
									// Fix for Alkem - Try to get UUID from different places in response
									uuid: fileUuid || response.data?.uuid || null,
									showUuid: true
							  }
							: mapping
					)
					console.log(
						"mappings after upload complete:",
						newMappings,
						"mappingId:",
						mappingId
					)
					return newMappings
				})
			}
		} catch (error) {
			console.error("Upload error:", error)
			if (isServier) {
				markServierFailed(getUploadErrorMessage(error))
				return
			}
			message.error(`Failed to upload ${file.name}: ${error.response?.data?.message || "Unknown error"}`)

			setMappings((prevMappings) => {
				const newMappings = prevMappings.map((mapping) =>
					mapping.id === mappingId ? { ...mapping, uploading: false, file: null, progress: 0 } : mapping
				)
				console.log("mappings after upload error reset:", newMappings, "mappingId:", mappingId)
				return newMappings
			})
		}
	}

	// Handle stockist selection for a row
	const handleStockistChange = (value, mappingId) => {
		setMappings((prevMappings) => {
			const newMappings = prevMappings.map((mapping) =>
				mapping.id === mappingId ? { ...mapping, stockistId: String(value) } : mapping
			)
			console.log("mappings after stockist update:", newMappings, "value:", value, "mappingId:", mappingId)
			return newMappings
		})
	}

	// Handle date selection for a row
	const handleDateChange = (date, mappingId) => {
		setMappings((prevMappings) => {
			const newMappings = prevMappings.map((mapping) =>
				mapping.id === mappingId ? { ...mapping, date } : mapping
			)
			console.log("mappings after date update:", newMappings, "date:", date, "mappingId:", mappingId)
			return newMappings
		})
	}

	// Servier: set statement type, month or financial year on a row
	const handleServierFieldChange = (field, value, mappingId) => {
		setMappings((prevMappings) =>
			prevMappings.map((mapping) => (mapping.id === mappingId ? { ...mapping, [field]: value } : mapping))
		)
	}

	// Values of uploading, uploaded or duplicate rows can't change: the backend has already received them
	const isRowLocked = (mapping) => mapping.uploading || Boolean(mapping.uuid) || Boolean(mapping.duplicateFile)

	// Servier: copy the "all files" values to every row that hasn't been uploaded yet
	const applyServierDefaultsToAll = () => {
		setMappings((prevMappings) =>
			prevMappings.map((mapping) => (isRowLocked(mapping) ? mapping : { ...mapping, ...servierDefaults }))
		)
	}

	// Servier: statement type, month and financial year selects, used by the "all files" bar and each row
	const renderServierSelects = (values, onChange, { labelSuffix = "", disabled = false, invalidFields = [] } = {}) => {
		const statusFor = (field) => (invalidFields.includes(field) ? "error" : undefined)
		return (
			<Space wrap>
				<Select
					aria-label={`Statement type${labelSuffix}`}
					placeholder="Statement type"
					value={values.statementType ?? undefined}
					onChange={(value) => onChange("statementType", value)}
					options={STATEMENT_TYPES.map((type) => ({ value: type, label: type }))}
					disabled={disabled}
					status={statusFor("statementType")}
					style={{ width: 150 }}
				/>
				<Select
					aria-label={`Month${labelSuffix}`}
					placeholder="Month"
					value={values.kennectMonth ?? undefined}
					onChange={(value) => onChange("kennectMonth", value)}
					options={MONTH_OPTIONS}
					disabled={disabled}
					status={statusFor("kennectMonth")}
					style={{ width: 130 }}
				/>
				<Select
					aria-label={`Financial year${labelSuffix}`}
					placeholder="Financial year"
					value={values.kennectYear ?? undefined}
					onChange={(value) => onChange("kennectYear", value)}
					options={financialYearOptions}
					disabled={disabled}
					status={statusFor("kennectYear")}
					style={{ width: 130 }}
				/>
			</Space>
		)
	}

	// Submit all mappings
	const handleSubmit = async () => {
		// Validate all mappings
		const invalidMappings = mappings.filter((mapping) => !mapping.uuid || !mapping.stockistId)
		console.log("mappings before submission:", mappings)
		console.log("Client name:", selectedClient.name)
		console.log("Invalid mappings:", invalidMappings)
		console.log("Submit button disabled condition:", selectedClient.name === "None" || mappings.some((m) => !m.uuid || !m.stockistId))

		if (invalidMappings.length > 0) {
			message.error("All rows must have both a file and a stockist selected")
			return
		}

		if (selectedClient.name === "None") {
			message.error("Please select a client first")
			return
		}

		// Prepare request payload
		const payload = {
			mappings: mappings.map((mapping) => ({
				uuid: mapping.uuid,
				stockistId: mapping.stockistId,
				date: mapping.date ? mapping.date.toISOString() : undefined,
			})),
		}

		try {
			// Use the client-specific host URL
			const mapEndpoint = `${selectedClient.hostUrl}/AdminUtility/v0.1/bulk-map-stockists`

			const response = await axios.post(mapEndpoint, payload, {
				headers: { Authorization: authToken },
			})

			if (response.data.ok) {
				message.success("Mappings submitted successfully")
				setResults(response.data.results)
				setActiveTab("2") // Switch to results tab
			} else {
				message.error("Failed to submit mappings")
			}
		} catch (error) {
			console.error("Submission error:", error)
			message.error(`Failed to submit mappings: ${error.response?.data?.message || "Unknown error"}`)
		}
	}

	// Results tab columns
	const successColumns = [
		{
			title: "File",
			dataIndex: "uuid",
			key: "uuid",
			render: (uuid) => {
				const mapping = mappings.find((m) => m.uuid === uuid)
				return mapping?.file?.name || "Unknown file"
			},
		},
		{
			title: "UUID",
			dataIndex: "uuid",
			key: "file_uuid",
			render: (uuid) => (
				<Text copyable style={{ fontSize: '12px' }}>{uuid}</Text>
			),
		},
		{
			title: "Stockist ID",
			dataIndex: "stockistId",
			key: "stockistId",
		},
		{
			title: "Status",
			key: "status",
			render: () => (
				<Text type="success">
					<CheckCircleOutlined /> Success
				</Text>
			),
		},
	]

	const errorColumns = [
		{
			title: "File",
			dataIndex: "uuid",
			key: "uuid",
			render: (uuid) => {
				const mapping = mappings.find((m) => m.uuid === uuid)
				return mapping?.file?.name || uuid
			},
		},
		{
			title: "Stockist ID",
			dataIndex: "stockistId",
			key: "stockistId",
		},
		{
			title: "Error",
			dataIndex: "error",
			key: "error",
		},
		{
			title: "Status",
			key: "status",
			render: () => (
				<Text type="danger">
					<CloseCircleOutlined /> Failed
				</Text>
			),
		},
	]

	// Effect to initialize stockist search when client changes
	useEffect(() => {
		if (selectedClient.name !== "None") {
			// Reset stockist options
			setStockistOptions([])

			// Reset stockist IDs in mappings when client changes
			setMappings((prevMappings) => {
				const newMappings = prevMappings.map((mapping) => ({
					...mapping,
					stockistId: null,
				}))
				console.log("Reset stockist IDs after client change:", newMappings)
				return newMappings
			})

			// Initialize with an empty search to get some stockists
			handleStockistSearch("")
		}
	}, [selectedClient, handleStockistSearch])


	return (
		<div className="upload-map-container" ref={containerRef}>
			<Tabs activeKey={activeTab} onChange={setActiveTab}>
				<TabPane tab="Upload & Map" key="1">
					<Space direction="vertical" size="large" className="full-width">
						<div className="client-selector">
							<Text strong>Target Client: {selectedClient.name}</Text>
							<Dropdown overlay={clientMenu}>
								<Button type="link" className="ant-dropdown-link" onClick={(e) => e.preventDefault()}>
									Change Client <DownOutlined />
								</Button>
							</Dropdown>
						</div>

						{isServier && (
							<div className="servier-defaults">
								<Text strong>Statement details for all files:</Text>
								{renderServierSelects(
									servierDefaults,
									(field, value) => setServierDefaults((prev) => ({ ...prev, [field]: value })),
									{ labelSuffix: " for all files" }
								)}
								<Button
									onClick={applyServierDefaultsToAll}
									disabled={getInvalidServierFields(servierDefaults).length > 0}
								>
									Apply to all files
								</Button>
							</div>
						)}

						<Title level={4}>File-Stockist Mapping</Title>

						<div className="mapping-container">
							{mappings.map((mapping, rowIndex) => {
								const invalidServierFields = isServier && mapping.showFieldErrors ? getInvalidServierFields(mapping) : []
								return (
								<div key={mapping.id} className="mapping-row">
									{isServier && (
										<div className="servier-fields">
											{renderServierSelects(
												mapping,
												(field, value) => handleServierFieldChange(field, value, mapping.id),
												{
													labelSuffix: ` for row ${rowIndex + 1}`,
													disabled: isRowLocked(mapping),
													invalidFields: invalidServierFields,
												}
											)}
											{invalidServierFields.length > 0 && (
												<Text type="danger" className="servier-fields-error">
													Required before upload:{" "}
													{invalidServierFields.map((field) => SERVIER_FIELD_LABELS[field]).join(", ")}
												</Text>
											)}
										</div>
									)}
									<div className="file-upload">
										<Upload
											beforeUpload={() => false}
											onChange={(info) => handleFileChange(info, mapping.id)}
											fileList={mapping.file ? [mapping.file] : []}
											disabled={mapping.uploading}
											showUploadList={false}
										>
											<Button
												icon={<UploadOutlined />}
												disabled={mapping.uploading}
											>
												Select File
											</Button>
										</Upload>
										{mapping.uploading && <Progress percent={mapping.progress} size="small" />}
										{mapping.file && !mapping.uploading && (
											<div>
												<Text ellipsis className="filename">
													{mapping.file.name}
												</Text>
												
												{/* Show file UUID if available */}
												{mapping.uuid && mapping.showUuid && (
													<div>
														<Text type="secondary" style={{ fontSize: '12px' }}>
															UUID: {mapping.uuid}
														</Text>
													</div>
												)}
												
												{/* Show duplicate file information */}
												{mapping.duplicateFile && mapping.duplicateUuid && (
													<div>
														<Text type="warning" style={{ fontSize: '12px' }}>
															Duplicate of: {mapping.duplicateUuid}
														</Text>
													</div>
												)}
												
												{/* Show why a Servier upload failed */}
												{mapping.uploadError && (
													<div>
														<Text type="danger" style={{ fontSize: '12px' }}>
															Failed: {mapping.uploadError}
														</Text>
													</div>
												)}

												{mapping.uploadError && (
													<Button
														type="link"
														size="small"
														style={{ padding: '0', marginTop: '4px', marginRight: '12px' }}
														onClick={() => uploadFile(mapping.file, mapping.id)}
													>
														Retry
													</Button>
												)}

												{/* Add reset button for duplicate or error cases */}
												{(mapping.duplicateFile || mapping.uuid || mapping.uploadError) && (
													<Button 
														type="link" 
														size="small"
														style={{ padding: '0', marginTop: '4px' }}
														onClick={(e) => resetFileUpload(e, mapping.id)}
													>
														Reset file
													</Button>
												)}	
											</div>
										)}
									</div>

									<div className="stockist-selector">
										<AutoComplete
											placeholder="Select a stockist"
											value={mapping.stockistId}
											onChange={(value) => handleStockistChange(value, mapping.id)}
											onSearch={handleStockistSearch}
											options={stockistOptions}
											disabled={selectedClient.name === "None"}
											style={{ width: "100%" }}
										/>
									</div>

									<div className="date-selector">
										<DatePicker
											onChange={(date) => handleDateChange(date, mapping.id)}
											value={mapping.date}
											placeholder="Select date (optional)"
										/>
									</div>

									<div className="row-actions">
										<Button
											type="text"
											danger
											onClick={() => removeRow(mapping.id)}
											disabled={mappings.length === 1}
										>
											Remove
										</Button>
									</div>
								</div>
								)
							})}
						</div>

						<div className="action-buttons">
							<Button type="dashed" icon={<PlusOutlined />} onClick={addNewRow}>
								Add Another Mapping
							</Button>

							<Button
								type="primary"
								onClick={handleSubmit}
								disabled={
									selectedClient.name === "None" || mappings.some((m) => !m.uuid || !m.stockistId)
								}
							>
								Submit All Mappings
							</Button>
						</div>


					</Space>
				</TabPane>

				<TabPane tab="Results" key="2">
					<div className="results-container">
						<Title level={4}>Mapping Results</Title>

						{results.success?.length > 0 || results.errors?.length > 0 ? (
							<>
								<div className="success-section">
									<Title level={5}>Successful Mappings</Title>
									{results.success?.length > 0 ? (
										<Table dataSource={results.success} columns={successColumns} rowKey="uuid" />
									) : (
										<Empty description="No successful mappings" />
									)}
								</div>

								<div className="error-section">
									<Title level={5}>Failed Mappings</Title>
									{results.errors?.length > 0 ? (
										<Table dataSource={results.errors} columns={errorColumns} rowKey="uuid" />
									) : (
										<Empty description="No failed mappings" />
									)}
								</div>
							</>
						) : (
							<Empty description="No results to display. Please submit mappings first." />
						)}

						<div className="action-buttons">
							<Button onClick={() => setActiveTab("1")}>Return to Upload</Button>
						</div>
					</div>
				</TabPane>
			</Tabs>
		</div>
	)
}

export default UploadAndMapStockists
